// components/ArborDrawer/CollapsibleSection.js
import { useState, useMemo } from "react";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Image from "next/image";
import SurveyIcon from "@/assets/survey.svg";
import HubspotIcon from "@/assets/hubspot.svg";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";

const NestedListItem = ({ name, items, onClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      <ListItemButton 
        onClick={() => setIsOpen(!isOpen)}
        sx={{ 
          pl: 4
        }}
      >
        <ListItemText 
          primary={name} 
          sx={{ color: "white" }}
        />
        {items.length > 0 && (
          isOpen ? <ArrowDropUpIcon htmlColor="white" /> : <ArrowDropDownIcon htmlColor="white" />
        )}
      </ListItemButton>
      
      {items.length > 0 && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {items.map((item) => (
              <ListItemButton
                sx={{ 
                  pl: 8
                }}
                onClick={() => onClick(item)}
                key={`${item.type}-${item.year}-${item.instance_id}`}
              >
                <ListItemText
                  primary={item.year || item.name}
                  sx={{ color: "white" }}
                  primaryTypographyProps={{
                    variant: "body2"
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const CollapsibleSection = ({ title, icon, items, onClick }) => {
  const [open, setOpen] = useState(true);
  
  const getIconComponent = (iconName) => {
    switch(iconName) {
      case "SurveyIcon":
        return SurveyIcon;
      case "HubspotIcon":
      default:
        return HubspotIcon;
    }
  };
  
  const IconComponent = getIconComponent(icon);
  
  // Group items by name
  const groupedItems = useMemo(() => {
    const groups = {};
    
    items.forEach(item => {
      // Check if we need to group this item
      if (items.filter(i => i.name === item.name).length > 1) {
        if (!groups[item.name]) {
          groups[item.name] = [];
        }
        groups[item.name].push(item);
      } else {
        // Single items get their own entry with an empty array
        groups[item.name] = [];
      }
    });
    
    // Convert to array format
    return Object.entries(groups).map(([name, children]: [string, any[]]) => ({
      name,
      items: children,
      // Take first item's properties for parent
      ...(children.length > 0 ? 
        { type: children[0].type, instance_id: children[0].instance_id } : 
        items.find(i => i.name === name))
    }));
  }, [items]);
  
  return (
    <>
      <ListItemButton
        sx={{
          minHeight: 48,
          px: 2
        }}
        onClick={() => setOpen(!open)}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            justifyContent: "center",
            color: "white",
          }}
        >
          <IconButton>
            <Image
              src={IconComponent}
              alt={title.toLowerCase()}
              width={20}
              height={20}
            />
          </IconButton>
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{
            fontWeight: "medium",
            variant: "body1",
            pl: 2,
          }}
          primary={title}
          sx={{ color: "white" }}
        />
        {open ? (
          <ArrowDropUpIcon htmlColor="white" />
        ) : (
          <ArrowDropDownIcon htmlColor="white" />
        )}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {groupedItems.map((group) => {
            if (group.items.length > 0) {
              return (
                <NestedListItem 
                  key={`group-${group.name}`}
                  name={group.name}
                  items={group.items}
                  onClick={onClick}
                />
              );
            } else {
              return (
                <ListItemButton
                  onClick={() => onClick(group)}
                  key={`${group.type}${group.instance_id ? '-' + group.instance_id : ''}`}
                  sx={{ pl: 4 }}
                >
                  <ListItemText
                    primary={group.name}
                    sx={{ color: "white" }}
                  />
                </ListItemButton>
              );
            }
          })}
        </List>
      </Collapse>
    </>
  );
};

export default CollapsibleSection;